import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {EventDocumentData, Simplify} from 'prismicio-types'
import {useMemo} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {Icon} from 'src/common/Icon'
import {useHubspotForm} from 'src/Event/hooks/useHubspotForm'
import {Input} from './components/Input'
import {Select} from './components/Select'
import {COUNTRIES, ROLES} from './constants/formOptions'

type Props = {
  data: Simplify<EventDocumentData>
  pageUid: string
}

export const RegisterForm = ({data, pageUid}: Props) => {
  const {form, submit, isSubmitSuccessful, isSubmitting} = useHubspotForm({
    formId: data.hubspot_form_id,
    pageUid,
    trackingLocation: 'component-event-register-form',
    trackDataType: 'event-registration',
    salesforce_campaign_id: data.salesforce_campaign_id,
  })

  const {handleSubmit, register, control, watch} = form
  const allValues = watch()
  const {linkResolver} = useLinkResolver()

  const formFields = useMemo(() => {
    if (!isFilled.group(data.form_fields)) {
      return null
    }

    const validFields = data.form_fields.filter((field) => isFilled.keyText(field.field_name))
    const totalFields = validFields.length
    const isOddCount = totalFields % 2 !== 0

    return validFields.map((field, index) => {
      const fieldType = field.field_type || 'text'
      const fieldName = field.field_name as string
      const isSelect = fieldType === 'select'
      const isLastField = index === totalFields - 1
      const isFirstTwoFields = index < 2

      const fieldClassName = cn({
        'col-span-1': isFirstTwoFields,
        'col-span-2': !isFirstTwoFields,
        'md:col-span-1': !(isOddCount && isLastField),
        'md:col-span-2': isOddCount && isLastField,
      })

      if (isSelect && fieldName === 'billing_country') {
        return (
          <Select
            key={fieldName}
            className={fieldClassName}
            placeholder={field.field_placeholder || 'Select country'}
            control={control}
            fieldName={fieldName}
            requiredMessage={field.required_validation_message || 'Please select a country.'}
            isRequired={field.is_required || false}
            options={COUNTRIES}
          />
        )
      }

      if (isSelect && fieldName === 'hs_persona') {
        return (
          <Select
            key={fieldName}
            className={fieldClassName}
            placeholder={field.field_placeholder || 'Select your role'}
            control={control}
            fieldName={fieldName}
            requiredMessage={field.required_validation_message || 'Please select your role.'}
            isRequired={field.is_required || false}
            options={ROLES}
          />
        )
      }

      return (
        <Input
          key={fieldName}
          className={fieldClassName}
          placeholder={field.field_placeholder || ''}
          register={register}
          fieldName={fieldName}
          type={fieldType}
          requiredMessage={field.required_validation_message || 'This field is required.'}
          isRequired={field.is_required || false}
          value={allValues[fieldName] as string}
          emailValidationMessage={
            fieldType === 'email' && isFilled.keyText(data.email_validation_text)
              ? data.email_validation_text
              : undefined
          }
        />
      )
    })
  }, [data.form_fields, allValues, control, register, data.email_validation_text])

  return (
    <Container
      className="mx-auto scroll-mt-16 overflow-visible px-5 !pb-0 !pt-10 sm:!pt-8 md:px-10 lg:px-5 xl:px-0"
      id="sign-up-section"
      backgroundColor="Grey/100"
    >
      <div
        className={cn(
          'flex w-full min-w-0 max-w-[1200px] flex-col gap-12 rounded-lg bg-green-dark p-6 md:gap-12 lg:flex-row lg:gap-16 lg:p-8',
        )}
      >
        <div className="flex w-full flex-col gap-3 lg:max-w-[670px] lg:flex-1 lg:gap-4">
          <h2 className="text-32 font-medium leading-[40px] text-moss-white md:leading-[38px]">
            {data.registration_headline}
          </h2>
          <p className="text-18 leading-6 text-moss-white/70 lg:text-15 lg:leading-[20px]">
            {data.registration_subheadline}
          </p>
        </div>

        <div className="flex w-full flex-col gap-6 lg:max-w-[680px] lg:flex-1 lg:gap-8">
          <form
            className={cn('grid w-full grid-cols-2 gap-3 text-moss-black', {
              hidden: isSubmitSuccessful,
            })}
            onSubmit={handleSubmit(submit)}
          >
            {formFields}

            {data.consent_text.length > 0 && (
              <label className="col-span-2 grid grid-flow-col justify-start gap-x-3 text-14 leading-5">
                <div className="grid">
                  <input
                    className="peer size-3.5 appearance-none rounded-[4px] border border-dcdbda bg-ffffff outline-none transition-shadow span-full placeholder:text-8f8d8b checked:border-green-default checked:bg-green-default focus:shadow-card"
                    type="checkbox"
                    {...register('checkbox', {
                      required: isFilled.keyText(data.consent_checkbox_validation_text)
                        ? data.consent_checkbox_validation_text
                        : 'Please confirm this.',
                    })}
                  />
                  <Icon className="size-3.5 text-moss-white span-full peer-checked:text-green-dark" name="tick" />
                </div>

                <PrismicRichText
                  components={{
                    paragraph: ({children}) => (
                      <p className="text-[11px] font-medium leading-[normal] tracking-[0.11px] text-moss-white">
                        {children}
                      </p>
                    ),
                    hyperlink: ({children, node}) => (
                      <Link
                        className="text-green-neon underline"
                        href={linkResolver(node.data)}
                        {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                      >
                        {children}
                      </Link>
                    ),
                  }}
                  field={data.consent_text}
                />
              </label>
            )}
            {isFilled.keyText(data.registration_button_text) && (
              <div className="col-span-2 pt-3 lg:pt-5">
                <ButtonV2
                  className="w-full"
                  variant="primary"
                  trackingData={{
                    mktgId: 'event-registration-submit-button',
                    location: `component-eregister-form`,
                    dataType: 'submit-button',
                    pageName: pageUid,
                    label: data.registration_button_text!,
                  }}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isFilled.keyText(data.button_submitting_text) && isSubmitting
                    ? data.button_submitting_text
                    : data.registration_button_text}
                </ButtonV2>
              </div>
            )}
          </form>

          <div
            className={cn(
              'flex h-[196px] w-full flex-col items-center justify-center gap-4 rounded-lg bg-moss-white/[7%] px-5 py-6 text-center',
              {
                hidden: !isSubmitSuccessful,
              },
            )}
          >
            <Icon className="size-12 text-green-neon" name="filled-check-v2" />
            <div className="flex flex-col text-center">
              <h3 className="text-tg-body-l mb-2 text-green-neon">{data.registration_success_title || 'Super!'}</h3>

              <PrismicRichText
                components={{
                  strong: ({children}) => <span className="text-tg-body-l text-green-neon">{children}</span>,
                  paragraph: ({children}) => <p className="text-tg-body-l text-moss-white">{children}</p>,
                  hyperlink: ({children, node}) => (
                    <Link
                      className="text-green-neon underline"
                      href={linkResolver(node.data)}
                      {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                    >
                      {children}
                    </Link>
                  ),
                }}
                field={data.success_description}
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}
